import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

export default function SmsSenderNumber() {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    phone_number: "",
    solapi_from: "",
    solapi_api_key: "",
    solapi_api_secret: "",
  });

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const readFunctionErrorMessage = async (error) => {
    const response = await error?.context?.clone?.().json().catch(() => null);
    return response?.error || error?.message || "";
  };

  const normalizeCredential = (value) => String(value || "").replace(/\s/g, "");

  const fetchNumber = async () => {
    const email = localStorage.getItem("email"); // ✅ 로그인한 이메일 기반
    if (!email) return;

    const { data, error } = await supabase.functions.invoke("sms-settings", { body: { action: "get" } });

    if (!error && data) {
      setSettings(data);
    } else {
      setSettings(null);
    }
  };

  const handleUpdate = async () => {
    const email = localStorage.getItem("email");
    if (!email) return;

    const phoneNumber = form.phone_number.trim();
    const solapiFrom = (form.solapi_from || form.phone_number).trim();
    if (!phoneNumber) {
      showUserAlert("발신번호를 입력해 주세요.");
      return;
    }
    const solapiApiKey = normalizeCredential(form.solapi_api_key);
    const solapiApiSecret = normalizeCredential(form.solapi_api_secret);
    if (!solapiApiKey && !settings?.api_key_registered) {
      showUserAlert("Solapi API Key를 입력해 주세요.");
      return;
    }
    if (solapiApiKey && solapiApiKey.length !== 16) {
      showUserAlert("Solapi API Key는 16자리입니다. Secret이 아니라 API Key 값을 입력해 주세요.");
      return;
    }
    if (!solapiApiSecret && !settings?.api_secret_registered) {
      showUserAlert("Solapi API Secret을 입력해 주세요.");
      return;
    }

    const payload = {
      action: "save",
      phone_number: phoneNumber,
      solapi_from: solapiFrom,
    };
    if (solapiApiKey) {
      payload.solapi_api_key = solapiApiKey;
    }
    if (solapiApiSecret) {
      payload.solapi_api_secret = solapiApiSecret;
    }

    const { error } = await supabase.functions.invoke("sms-settings", { body: payload });
    if (error) {
      const message = await readFunctionErrorMessage(error);
      showUserAlert("수정 실패: " + (message || error.message));
    } else {
      showUserAlert("번호가 수정되었습니다.");
      setEditing(false);
      setForm({ phone_number: "", solapi_from: "", solapi_api_key: "", solapi_api_secret: "" });
      fetchNumber();
    }
  };

  useEffect(() => {
    fetchNumber();
  }, []);

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4">
        <h2 className="text-lg font-semibold">SMS 문자 발신 번호</h2>
        <Card className="p-4">
          <CardContent className="bg-white">
            <div className="mb-3 rounded bg-blue-50 px-3 py-2 text-sm text-blue-800">
              현재 당사는 Solapi 문자 서비스를 이용하고 있습니다. 발신번호는 해당 Solapi 계정에 등록된 번호여야 합니다. 처음 사용하시는 경우{" "}
              <a
                href="https://solapi.com/start"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline"
              >
                Solapi 가입 화면
              </a>
              에서 가입 및 발신번호 등록을 진행해 주세요.
            </div>
            {!editing ? (
              <div className="space-y-2 text-sm">
                <div>
                  <a
                    href="/solapi-api-key-guide.pdf"
                    download="솔라피 API Key 등록 가이드.pdf"
                    className="font-semibold text-blue-600 underline"
                  >
                    Solapi API 받는 방법 가이드
                  </a>
                </div>
                <div>
                  <span className="font-semibold">발신번호: </span>
                  <button
                    type="button"
                    className="text-blue-600 underline"
                    onClick={() => {
                      setEditing(true);
                      setForm({
                        phone_number: settings?.phone_number || "",
                        solapi_from: settings?.solapi_from || settings?.phone_number || "",
                        solapi_api_key: "",
                        solapi_api_secret: "",
                      });
                    }}
                  >
                    {settings?.phone_number ? settings.phone_number : "번호 없음"}
                  </button>
                </div>
                <div>
                  <span className="font-semibold">Solapi API Key: </span>
                  {settings?.api_key_registered ? "등록됨" : "미등록"}
                </div>
                <div>
                  <span className="font-semibold">Solapi API Secret: </span>
                  {settings?.api_secret_registered ? "등록됨" : "미등록"}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  발신번호
                </label>
                <Input
                  value={form.phone_number}
                  onChange={(e) => updateForm("phone_number", e.target.value)}
                  placeholder="예: 01012345678"
                />
                <label className="block text-sm font-medium">
                  Solapi 발신번호
                </label>
                <Input
                  value={form.solapi_from}
                  onChange={(e) => updateForm("solapi_from", e.target.value)}
                  placeholder="비워두면 위 발신번호를 사용합니다."
                />
                <label className="block text-sm font-medium">
                  Solapi API Key
                </label>
                <Input
                  value={form.solapi_api_key}
                  onChange={(e) => updateForm("solapi_api_key", e.target.value)}
                  placeholder={settings?.api_key_registered ? "변경할 때만 새 Key를 입력하세요." : "Solapi API Key"}
                />
                <label className="block text-sm font-medium">
                  Solapi API Secret
                </label>
                <Input
                  type="password"
                  value={form.solapi_api_secret}
                  onChange={(e) => updateForm("solapi_api_secret", e.target.value)}
                  placeholder={settings?.api_secret_registered ? "변경할 때만 새 Secret을 입력하세요." : "Solapi API Secret"}
                />
                <div className="flex gap-2">
                  <Button onClick={handleUpdate}>수정</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>돌아가기</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
